import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAtomValue } from 'jotai';

import { batchViewAtom } from '@/_atoms/batch';
import { getNextPage } from '@/_helpers/loginHelpers';

/**
 * Redirects authenticated users away from the login flow.
 *
 * This hook observes the current `BatchView` and automatically navigates to the appropriate page
 * once BatchView data is available.
 * 1. Batch includes a welcome → navigate to `/welcome`.
 * 2. Exactly one material → navigate directly to that survey.
 * 3. Default → navigate to the batch overview page.
 */
export function useLoginRedirect() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useAtomValue(batchViewAtom);

  // Optional: prevents double navigation in React 18 StrictMode dev
  const didNavigate = useRef(false);

  useEffect(() => {
    if (!data || isLoading || error || didNavigate.current) return;

    didNavigate.current = true;

    const nextPage: string = getNextPage(data);

    navigate(nextPage, { replace: true });
  }, [data, isLoading, error, navigate]);
}